import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppLayout from "@/components/AppLayout";
import { Toaster } from "@/components/ui/sonner";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Shipments from "@/pages/Shipments";
import Invoices from "@/pages/Invoices";
import Expenses from "@/pages/Expenses";
import Customers from "@/pages/Customers";
import Vehicles from "@/pages/Vehicles";
import VehiclesTracking from "@/pages/VehiclesTracking";
import BookingReport from "@/pages/BookingReport";
import MISReport from "@/pages/MISReport";
import DeliveryReport from "@/pages/DeliveryReport";
import Drivers from "@/pages/Drivers";
import Settings from "@/pages/Settings";
import MoneyReceipts from "@/pages/MoneyReceipts";
import DebitVouchers from "@/pages/DebitVouchers";
import Unbilled from "@/pages/Unbilled";
import Outstanding from "@/pages/Outstanding";
import ConsignmentEntry from "@/pages/ConsignmentEntry";
import ConsignmentEnquiry from "@/pages/ConsignmentEnquiry";
import ChangeRequestLog from "@/pages/ChangeRequestLog";
import PODUpload from "@/pages/PODUpload";
import BillUpload from "@/pages/BillUpload";
import EWayBills from "@/pages/EWayBills";
import BillCreate from "@/pages/BillCreate";
import BillSubmission from "@/pages/BillSubmission";
import DriverMaster from "@/pages/DriverMaster";
import VendorMaster from "@/pages/VendorMaster";
import VehicleMaster from "@/pages/VehicleMaster";
import AssetBook from "@/pages/AssetBook";
import LogBook from "@/pages/LogBook";
import FreightMaster from "@/pages/FreightMaster";
import Circulars from "@/pages/Circulars";
import VendorLedger from "@/pages/VendorLedger";
import DayBook from "@/pages/DayBook";
import CustomerLedger from "@/pages/CustomerLedger";
import Stationary from "@/pages/Stationary";
import LetterOfAccount from "@/pages/LetterOfAccount";
import VehicleHireChallan from "@/pages/VehicleHireChallan";
import MarketVehicle from "@/pages/MarketVehicle";
import StaffSalary from "@/pages/StaffSalary";
import BankStatement from "@/pages/BankStatement";
import Bills from "@/pages/Bills";
import BillEnquiry from "@/pages/BillEnquiry";
import Reminders from "@/pages/Reminders";
import StaffLogins from "@/pages/StaffLogins";
import AdminPanel from "@/pages/AdminPanel";
import Attendance from "@/pages/Attendance";
import LeaveApplications from "@/pages/LeaveApplications";
import LetterPad from "@/pages/LetterPad";
import NTCCalendar from "@/pages/NTCCalendar";
import NTCProfile from "@/pages/NTCProfile";
import StaffMaster from "@/pages/StaffMaster";
import RouteProfit from "@/pages/RouteProfit";

const withLayout = (Page) => (
  <ProtectedRoute>
    <AppLayout>
      <Page />
    </AppLayout>
  </ProtectedRoute>
);

function App() {
  useEffect(() => {
    const current = Array.from(document.scripts).map((s) => s.src).find((s) => s.includes("/static/js/main"));
    if (!current) return;
    let reloading = false;
    const check = async () => {
      try {
        const html = await fetch("/", { cache: "no-store" }).then((r) => r.text());
        const m = html.match(/static\/js\/main[^"]*\.js/);
        if (m && !current.includes(m[0]) && !reloading) {
          reloading = true;
          window.location.reload();
        }
      } catch {}
    };
    const iv = setInterval(check, 5 * 60 * 1000);
    window.addEventListener("focus", check);
    check();
    return () => {
      clearInterval(iv);
      window.removeEventListener("focus", check);
    };
  }, []);
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/" element={withLayout(Dashboard)} />
            <Route path="/ntc-profile" element={withLayout(NTCProfile)} />
            <Route path="/consignment-entry" element={withLayout(ConsignmentEntry)} />
            <Route path="/consignment-enquiry" element={withLayout(ConsignmentEnquiry)} />
            <Route path="/correction-log" element={withLayout(ChangeRequestLog)} />
            <Route path="/pod-upload" element={withLayout(PODUpload)} />
            <Route path="/bill-upload" element={withLayout(BillUpload)} />
            <Route path="/eway-bills" element={withLayout(EWayBills)} />
            <Route path="/bill-create" element={withLayout(BillCreate)} />
            <Route path="/bill-submission" element={withLayout(BillSubmission)} />
            <Route path="/bills" element={withLayout(Bills)} />
            <Route path="/bill-enquiry" element={withLayout(BillEnquiry)} />
            <Route path="/route-profit" element={withLayout(RouteProfit)} />
            <Route path="/reminders" element={withLayout(Reminders)} />
            <Route path="/staff-logins" element={withLayout(StaffLogins)} />
            <Route path="/admin-panel" element={withLayout(AdminPanel)} />
            <Route path="/attendance" element={withLayout(Attendance)} />
            <Route path="/leave-applications" element={withLayout(LeaveApplications)} />
            <Route path="/letter-pad" element={withLayout(LetterPad)} />
            <Route path="/staff-master" element={withLayout(StaffMaster)} />
            <Route path="/ntc-calendar" element={withLayout(NTCCalendar)} />
            <Route path="/driver-master" element={withLayout(DriverMaster)} />
            <Route path="/vendor-master" element={withLayout(VendorMaster)} />
            <Route path="/vehicle-master" element={withLayout(VehicleMaster)} />
            <Route path="/asset-book" element={withLayout(AssetBook)} />
            <Route path="/log-book" element={withLayout(LogBook)} />
            <Route path="/freight-master" element={withLayout(FreightMaster)} />
            <Route path="/circulars" element={withLayout(Circulars)} />
            <Route path="/vendor-ledger" element={withLayout(VendorLedger)} />
            <Route path="/day-book" element={withLayout(DayBook)} />
            <Route path="/customer-ledger" element={withLayout(CustomerLedger)} />
            <Route path="/stationary" element={withLayout(Stationary)} />
            <Route path="/letter-of-account" element={withLayout(LetterOfAccount)} />
            <Route path="/shipments" element={withLayout(Shipments)} />
            <Route path="/invoices" element={withLayout(Invoices)} />
            <Route path="/receipts" element={withLayout(MoneyReceipts)} />
            <Route path="/vouchers" element={withLayout(DebitVouchers)} />
            <Route path="/hire-challans" element={withLayout(VehicleHireChallan)} />
            <Route path="/market-vehicles" element={withLayout(MarketVehicle)} />
            <Route path="/salaries" element={withLayout(StaffSalary)} />
            <Route path="/bank-statement" element={withLayout(BankStatement)} />
            <Route path="/unbilled" element={withLayout(Unbilled)} />
            <Route path="/outstanding" element={withLayout(Outstanding)} />
            <Route path="/expenses" element={withLayout(Expenses)} />
            <Route path="/customers" element={withLayout(Customers)} />
            <Route path="/vehicles" element={withLayout(Vehicles)} />
            <Route path="/vehicles-tracking" element={withLayout(VehiclesTracking)} />
            <Route path="/booking-report" element={withLayout(BookingReport)} />
            <Route path="/mis-report" element={withLayout(MISReport)} />
            <Route path="/delivery-report" element={withLayout(DeliveryReport)} />
            <Route path="/drivers" element={withLayout(Drivers)} />
            <Route path="/settings" element={withLayout(Settings)} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </div>
  );
}

export default App;
